import { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n, type TranslationKey } from "@/lib/i18n";
import { SessionContext } from "@/features/auth/SessionContext";
import { useBreadcrumbLabels } from "./useBreadcrumbLabels";

/** Route segment display names. */
const SEGMENT_LABELS: Record<string, TranslationKey> = {
  admin: "breadcrumb.admin",
  ops: "breadcrumb.ops",
  client: "breadcrumb.client",
  dashboard: "breadcrumb.dashboard",
  catalog: "breadcrumb.catalog",
  capabilities: "breadcrumb.capabilities",
  definitions: "breadcrumb.definitions",
  "operational-contracts": "breadcrumb.operationalContracts",
  dictionaries: "breadcrumb.dictionaries",
  projects: "breadcrumb.projects",
  programs: "breadcrumb.programs",
  twins: "breadcrumb.twins",
  provisioning: "breadcrumb.provisioning",
  simulator: "breadcrumb.simulator",
  operations: "breadcrumb.operations",
  "deployment-units": "breadcrumb.deploymentUnits",
  assets: "breadcrumb.assets",
  assignments: "breadcrumb.assignments",
  "field-deployment": "breadcrumb.fieldDeployment",
  reservations: "breadcrumb.reservations",
  commissioning: "breadcrumb.commissioning",
  workflow: "breadcrumb.workflow",
  garage: "breadcrumb.garage",
  runtime: "breadcrumb.runtime",
  instances: "breadcrumb.instances",
  bindings: "breadcrumb.bindings",
  dashboards: "breadcrumb.dashboards",
  geo: "breadcrumb.geo",
  organizations: "breadcrumb.organizations",
  users: "breadcrumb.users",
  workspaces: "breadcrumb.workspaces",
  partnerships: "breadcrumb.partnerships",
  settings: "breadcrumb.settings",
  reports: "breadcrumb.reports",
  support: "breadcrumb.support",
  monitoring: "breadcrumb.monitoring",
  deployments: "breadcrumb.deployments",
  new: "breadcrumb.new",
  edit: "breadcrumb.edit",
  payload: "breadcrumb.payload",
  conversions: "breadcrumb.conversions",
  sites: "breadcrumb.sites",
  scopes: "breadcrumb.scopes",
};

/** UUID pattern for filtering out IDs from breadcrumbs display. */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatSegment(
  segment: string,
  t: (key: TranslationKey) => string,
): string {
  if (UUID_PATTERN.test(segment)) return segment.substring(0, 8) + "...";
  const labelKey = SEGMENT_LABELS[segment];
  if (labelKey) return t(labelKey);
  return segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Breadcrumbs() {
  const { t } = useI18n();
  const location = useLocation();
  // The name a page gave to an id of the path (QA-07), e.g. the project's.
  const labels = useBreadcrumbLabels();
  // Read the session defensively: Breadcrumbs is also rendered in contexts
  // (and tests) that are not wrapped in a SessionProvider.
  const sessionState = useContext(SessionContext);
  const session = sessionState?.session ?? null;
  const segments = location.pathname.split("/").filter(Boolean);

  if (segments.length <= 1) return null;

  // Tenant context prefix — only when bootstrap resolved a real workspace.
  const activeWorkspaceName = session?.active_workspace?.name ?? null;
  const activeOrganizationName = session?.active_organization?.name ?? null;
  const contextLabel =
    activeWorkspaceName && activeOrganizationName
      ? `${activeOrganizationName} › ${activeWorkspaceName}`
      : null;

  return (
    <nav
      aria-label={t("common.breadcrumb")}
      className="flex min-w-0 flex-wrap items-center gap-1 text-[13px]"
    >
      {contextLabel && (
        <>
          <span className="max-w-full truncate font-medium text-[var(--fg-muted)] sm:max-w-[280px]">
            {contextLabel}
          </span>
          <span className="text-[var(--gray-sb-200)]">·</span>
        </>
      )}
      <Link
        to="/"
        className="text-[var(--fg-muted)] transition-colors hover:text-[var(--fg)]"
      >
        <Home className="h-3.5 w-3.5" aria-label={t("common.home")} />
      </Link>
      {segments.map((segment, index) => {
        const path = "/" + segments.slice(0, index + 1).join("/");
        const isLast = index === segments.length - 1;
        const name = labels.get(segment);
        const label = name ?? formatSegment(segment, t);
        // A name can be long: cut on screen, whole in the tooltip.
        const named = name ? { title: name } : {};

        return (
          <span key={path} className="flex min-w-0 items-center gap-1">
            <span className="text-[var(--gray-sb-200)]">/</span>
            {isLast ? (
              <span
                {...named}
                className={cn(
                  "font-medium text-[var(--fg)]",
                  name && "max-w-64 min-w-0 truncate",
                )}
              >
                {label}
              </span>
            ) : (
              <Link
                to={path}
                {...named}
                className={cn(
                  "text-[var(--fg-muted)] transition-colors hover:text-[var(--brand-primary)]",
                  name && "max-w-64 min-w-0 truncate",
                )}
              >
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
